import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { DevicesResponse } from '../../../NonReact/Types/Types'
import {
    LANGUAGE_DEFAULT,
    REPORTS_CREATE,
    REPORTS_DELETE,
    REPORTS_READONLY,
    REPORTS_UPDATE,
    SERVER_ALL_VALUE,
    SERVER_NONE_CRI
} from '../../AddUser/Constants'
import { ReportingAccessLevels } from '../../AddUser/Reporting/ReportingAccessLevels'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { getRandomPassword } from '../../Common/Components/PasswordTextInput/PasswordTextInput'
import { UserDefault, UserDefaults } from '../../Common/Types'
import { encrypt } from '../../Common/Utils/Security'
import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { DEFAULT_TIMEZONE } from '../../TimezoneModal/Constants/Constants'
import { AdminUserBody } from '../../../NonReact/common/Types/Types'
import {
    CxIntegrationLicence,
    SelfProvisioningUser
} from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'

/**
 * Map data from the user form to the user object, to be used in the API.
 *
 * @param config - The config object.
 * @param user - The user object.
 * @param cmeID - The CME ID.
 * @param telSysID - The TelSys ID.
 * @param partitionID - The partition ID.
 * @param device - The device object.
 *
 * @returns The user request object for the user POST API.
 */
export const userFormStateMapper = async (
    userDefaults: UserDefaults,
    { email, name, userType, welcomeEmail, useEmailAsUsername, cxiLicence }: SelfProvisioningUser,
    cmeID: string,
    telSysID: number,
    partitionID: number,
    sessionNonce?: string,
    device?: DevicesResponse,
    isAgent?: boolean,
    currentUsername?: string,
    username?: string
): Promise<AdminUserBody> => {
    const userDefaultsForType: Nullable<UserDefault> = userDefaults?.[userType]
    const requiresEndpoint: boolean = userDefaultsForType?.requiresEndpoint ?? false
    const isSelfScoped: boolean = userDefaultsForType?.isSelfScoped ?? false
    const userName: string = username ?? (!useEmailAsUsername ? device?.TelSysUsername ?? EMPTY_STRING : email)
    const isDowngrade: boolean = !!(
        isAgent &&
        userDefaultsForType?.licenceLevel !== ReportingAccessLevels.CALL_CENTRE_AGENT &&
        userDefaultsForType?.licenceLevel !== ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
    )
    const isAgentCalculated: boolean = isDowngrade ? false : isAgent ?? false
    const isNewUsername: boolean = !!(currentUsername && currentUsername !== userName)
    const needsNewPassword: boolean = IsStringEmpty(currentUsername) || isNewUsername

    return {
        Username: currentUsername ?? userName,
        UsernameNew: isNewUsername ? userName : undefined,
        EmailAddr: email,
        DefaultLang: LANGUAGE_DEFAULT,
        FullName: device?.Description ?? name ?? EMPTY_STRING,
        LockOut: false,
        SendWelcomeEmail: needsNewPassword ? welcomeEmail : false,
        PasswordChgRequired: true,
        Password: needsNewPassword ? encrypt(await getRandomPassword(), sessionNonce) : undefined,
        EnableMFA: false,
        Advanced: {
            ReportUpdInterval: ALL_VALUE
        },
        TimeZone: DEFAULT_TIMEZONE,
        Scope: {
            CMEnvironmentID: cmeID,
            TelSysID: [telSysID],
            PartIDInTelSys: [partitionID],
            DeviceNumber: isSelfScoped ? device?.DeviceNumber ?? SERVER_ALL_VALUE : SERVER_ALL_VALUE,
            AgentNumber:
                isSelfScoped && isAgentCalculated ? device?.DeviceNumber ?? SERVER_ALL_VALUE : SERVER_ALL_VALUE,
            CallRecIntID: SERVER_NONE_CRI,
            ReportingAccess: userDefaultsForType?.licenceLevel ?? EMPTY_STRING,
            EnhancedMobileReporting: !!userDefaultsForType?.mobileAppEnabled,
            DefaultReportCollection: userDefaultsForType?.defaultReportsCollection,
            CXILicenseType: (cxiLicence?.toUpperCase() as CxIntegrationLicence) ?? CxIntegrationLicence.NONE
        },
        Endpoint: {
            TelSysID: requiresEndpoint ? telSysID : null,
            PartIDInTelSys: requiresEndpoint ? partitionID : null,
            DeviceNumber: requiresEndpoint && !isAgentCalculated ? device?.DeviceNumber ?? null : null,
            AgentNumber: requiresEndpoint && isAgentCalculated ? device?.DeviceNumber ?? null : null
        },
        Permissions: {
            Admin: [],
            NonAdmin: !userDefaultsForType?.allowEmailAsUsername
                ? [REPORTS_READONLY]
                : [REPORTS_READONLY, REPORTS_UPDATE, REPORTS_CREATE, REPORTS_DELETE],
            Other: []
        }
    }
}
